import type { Guest } from '~/@entities/Guest'
import type { LessonClassId } from '~/@entities/LessonClass'
import type { NumberGradeElementary, SchoolType } from '~/@entities/SchoolSystem'
import type { Student } from '~/@entities/Student'
import type {
  AssignedCasDetailResponse,
  AssignedCasStudentScoringItemResponse,
  AssignedCasWithHomeworkResponse,
  CasAssignmentCancellationRequest,
  CasAssignmentRequest,
  CasAssignmentResponse,
  CasBulkDeleteRequest,
  CasCreationRequest,
  CasDetailResponse,
  CasModificationToHiddenRequest,
  CasResponse,
  CasSubmitRequest,
  CasSubmitResponse,
  CasUpdateRequest,
  CreverseEntranceCasAssignmentCancellationRequest,
  CreverseEntranceCasAssignmentRequest,
  CreverseEntranceCasBulkDeleteRequest,
  CreverseEntranceCasCreateRequest,
  CreverseEntranceCasDetailResponse,
  CreverseEntranceCasResponse,
  CreverseEntranceCasUpdateRequest,
  GetAssignedCasesForStudentParams,
  GetAssignedCasesParams,
  GetGroupAssignedCasesByGradeParams,
  GetGroupAssignedCasesByLessonClassParams,
  GetListParams,
  GetScoring1Data,
  GetScoringData,
  GetSetsParams,
  GetStudentCasHomeworksParams,
  Pageable,
  SliceAssignedCasItemResponse,
  SliceCasSummaryResponse,
  SliceCreverseEntranceCasSummaryResponse,
  SliceCreverseGuestEntranceCasItemResponse,
  SliceGroupAssignedCasItemResponse,
  SliceGroupAssignedCasScoringResponse,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptCasAssignedIds,
  adaptCasAssignmentDetail,
  adaptCasDetail,
  adaptCases,
  adaptCasHomeworkAssignments,
  adaptCasRef,
  adaptCasScoringItems,
  adaptCasSolvedProblems,
  adaptCasStudentScorings,
  adaptCasSubmitResult,
  adaptEntranceCases,
  adaptGroupAssignedCases,
  adaptGuestEntranceCases,
  adaptStudentAssignedCases,
} from './adapters'
import type {
  Cas,
  CasAssignedStudent,
  CasAssignmentDetail,
  CasDetail,
  CasHomeworkAssignment,
  CasScoringItem,
  CasSolvedProblem,
  CasStudentScoring,
  CasSubmitResult,
  EntranceCas,
  EntranceCasAssignee,
  GroupAssignedCas,
  GuestEntranceCas,
  StudentAssignedCas,
} from './schemas'

// 이 엔티티의 컨트롤러들은 CommonResponse 봉투 없이 본문을 바로 낸다.
// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.

export type CasListParams = Omit<GetSetsParams, 'pageable'> & Pageable
export type CasCreateBody = CasCreationRequest
export type CasUpdateBody = CasUpdateRequest
export type CasAssignBody = CasAssignmentRequest
export type CasUnassignBody = CasAssignmentCancellationRequest
export type CasDeleteBody = CasBulkDeleteRequest
export type CasHiddenBody = CasModificationToHiddenRequest
export type GroupAssignedCasListParams = Omit<
  GetGroupAssignedCasesByLessonClassParams,
  'pageable' | 'lessonClassId'
> &
  Pageable
export type StudentAssignedCasListParams = Omit<
  GetAssignedCasesForStudentParams,
  'pageable' | 'studentId'
> &
  Pageable
export type CasHomeworkListParams = Omit<GetStudentCasHomeworksParams, 'studentId'>
// 서버 CasSubmitRequest.userAnswers 는 List<String>? 로 선언됐지만 원소의 null 을 미입력 빈칸으로 받아 오답
// 처리한다(jackson-module-kotlin 은 컬렉션 원소의 null 을 검사하지 않는다). 스펙이 이 null 을 그리지 못해
// 원소 타입만 되살린다. 2026-10-07 백엔드 develop 의 cas/controller/request/CasSubmitRequest.kt 와 학생 앱의
// 다중 셀 제출 동작으로 확인.
export type CasSubmitBody = Omit<WithNullableOptionals<CasSubmitRequest>, 'userAnswers'> & {
  userAnswers?: readonly (string | null)[] | null
}

export type EntranceCasListParams = Omit<GetListParams, 'pageable'> & Pageable
export type EntranceCasCreateBody = CreverseEntranceCasCreateRequest
export type EntranceCasUpdateBody = CreverseEntranceCasUpdateRequest
export type EntranceCasAssignBody = CreverseEntranceCasAssignmentRequest
export type EntranceCasUnassignBody = CreverseEntranceCasAssignmentCancellationRequest
export type EntranceCasDeleteBody = CreverseEntranceCasBulkDeleteRequest
export type GuestEntranceCasListParams = Omit<GetAssignedCasesParams, 'guestId'>

/***************** 자동시험 시험지 *****************/

export const getCases = async (params: CasListParams, meta?: RequestMeta): Promise<Slice<Cas>> =>
  adaptCases(await httpClient().get<SliceCasSummaryResponse>('/cas', { params, meta }))

export const getCasDetail = async (casId: Cas['casId'], meta?: RequestMeta): Promise<CasDetail> =>
  adaptCasDetail(await httpClient().get<CasDetailResponse>(`/cas/${casId}`, { meta }))

export const createCas = async (body: CasCreateBody, meta?: RequestMeta): Promise<Cas['casId']> =>
  adaptCasRef(await httpClient().post<CasResponse>('/cas', body, { meta }))

export const updateCas = async (
  casId: Cas['casId'],
  body: CasUpdateBody,
  meta?: RequestMeta,
): Promise<Cas['casId']> =>
  adaptCasRef(await httpClient().put<CasResponse>(`/cas/${casId}`, body, { meta }))

// 단건도 배열 하나로 지운다
export const deleteCases = async (body: CasDeleteBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().delete('/cas', { data: body, meta })
}

export const updateCasHiddenFromStudent = async (
  body: CasHiddenBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch('/cas/hidden', body, { meta })
}

/***************** 재원생 배정 *****************/

// 이미 활성인 배정이 섞여도 서버가 멱등으로 처리하고, 새로 만든 것과 기존 것의 배정 id 를 함께 돌려준다
export const assignCas = async (
  body: CasAssignBody,
  meta?: RequestMeta,
): Promise<CasAssignedStudent['assignedId'][]> => {
  const result = await httpClient().post<CasAssignmentResponse>('/cas/assignments', body, { meta })
  return adaptCasAssignedIds(result)
}

export const unassignCas = async (body: CasUnassignBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().delete('/cas/assignments', { data: body, meta })
}

export const getGroupAssignedCasesByLessonClass = async (
  lessonClassId: LessonClassId,
  params: GroupAssignedCasListParams,
  meta?: RequestMeta,
): Promise<Slice<GroupAssignedCas>> =>
  adaptGroupAssignedCases(
    await httpClient().get<SliceGroupAssignedCasItemResponse>(
      `/cas/assignments/lesson-class/${lessonClassId}`,
      { params, meta },
    ),
  )

export const getGroupAssignedCasesByGrade = async (
  schoolType: SchoolType,
  grade: NumberGradeElementary,
  params: Omit<GetGroupAssignedCasesByGradeParams, 'pageable' | 'schoolType' | 'grade'> & Pageable,
  meta?: RequestMeta,
): Promise<Slice<GroupAssignedCas>> =>
  adaptGroupAssignedCases(
    await httpClient().get<SliceGroupAssignedCasItemResponse>(
      `/cas/assignments/grade/${schoolType}/${grade}`,
      { params, meta },
    ),
  )

export const getStudentAssignedCases = async (
  studentId: Student['id'],
  params: StudentAssignedCasListParams,
  meta?: RequestMeta,
): Promise<Slice<StudentAssignedCas>> =>
  adaptStudentAssignedCases(
    await httpClient().get<SliceAssignedCasItemResponse>(`/cas/assignments/students/${studentId}`, {
      params,
      meta,
    }),
  )

/***************** 채점 *****************/

export const getCasScoringItems = async (
  assignedCasId: CasAssignedStudent['assignedId'],
  meta?: RequestMeta,
): Promise<CasScoringItem[]> =>
  adaptCasScoringItems(
    await httpClient().get<GetScoring1Data>(`/cas/assignments/${assignedCasId}/scoring`, { meta }),
  )

export const getCasStudentScoringsByLessonClass = async (
  lessonClassId: LessonClassId,
  casId: Cas['casId'],
  meta?: RequestMeta,
): Promise<Slice<CasStudentScoring>> =>
  adaptCasStudentScorings(
    await httpClient().get<SliceGroupAssignedCasScoringResponse>(
      `/cas/assignments/lesson-class/${lessonClassId}/${casId}/scoring`,
      { meta },
    ),
  )

export const getCasStudentScoringsByGrade = async (
  schoolType: SchoolType,
  grade: NumberGradeElementary,
  casId: Cas['casId'],
  meta?: RequestMeta,
): Promise<Slice<CasStudentScoring>> =>
  adaptCasStudentScorings(
    await httpClient().get<SliceGroupAssignedCasScoringResponse>(
      `/cas/assignments/grade/${schoolType}/${grade}/${casId}/scoring`,
      { meta },
    ),
  )

/***************** 학생 응시 *****************/

export const getCasHomeworkAssignments = async (
  studentId: Student['id'],
  params: CasHomeworkListParams,
  meta?: RequestMeta,
): Promise<CasHomeworkAssignment[]> =>
  adaptCasHomeworkAssignments(
    await httpClient().get<AssignedCasWithHomeworkResponse[]>(
      `/cas/assignments/students/${studentId}/homeworks`,
      { params, meta },
    ),
  ) ?? []

export const getCasAssignmentDetail = async (
  assignedId: CasAssignmentDetail['assignedId'],
  meta?: RequestMeta,
): Promise<CasAssignmentDetail> =>
  adaptCasAssignmentDetail(
    await httpClient().get<AssignedCasDetailResponse>(`/cas/assignments/${assignedId}`, { meta }),
  )

export const getCasSolvedProblems = async (
  assignedId: CasAssignmentDetail['assignedId'],
  meta?: RequestMeta,
): Promise<CasSolvedProblem[]> =>
  adaptCasSolvedProblems(
    await httpClient().get<AssignedCasStudentScoringItemResponse[]>(
      `/cas/assignments/${assignedId}/problem`,
      { meta },
    ),
  ) ?? []

export const submitCasAnswer = async (
  assignedId: CasAssignmentDetail['assignedId'],
  body: CasSubmitBody,
  meta?: RequestMeta,
): Promise<CasSubmitResult> =>
  adaptCasSubmitResult(
    await httpClient().post<CasSubmitResponse>(`/cas/assignments/${assignedId}/submit`, body, {
      meta,
    }),
  )

/***************** 입학고사 *****************/

export const getEntranceCases = async (
  params: EntranceCasListParams,
  meta?: RequestMeta,
): Promise<Slice<EntranceCas>> =>
  adaptEntranceCases(
    await httpClient().get<SliceCreverseEntranceCasSummaryResponse>('/creverse/entrance-cas', {
      params,
      meta,
    }),
  )

export const getEntranceCasDetail = async (
  casId: EntranceCas['casId'],
  meta?: RequestMeta,
): Promise<CasDetail> =>
  adaptCasDetail(
    await httpClient().get<CreverseEntranceCasDetailResponse>(`/creverse/entrance-cas/${casId}`, {
      meta,
    }),
  )

// 문제 수는 서버가 영역 수로 나눠 정한다
export const createEntranceCas = async (
  body: EntranceCasCreateBody,
  meta?: RequestMeta,
): Promise<EntranceCas['casId']> =>
  adaptCasRef(
    await httpClient().post<CreverseEntranceCasResponse>('/creverse/entrance-cas', body, { meta }),
  )

export const updateEntranceCas = async (
  casId: EntranceCas['casId'],
  body: EntranceCasUpdateBody,
  meta?: RequestMeta,
): Promise<EntranceCas['casId']> =>
  adaptCasRef(
    await httpClient().put<CreverseEntranceCasResponse>(`/creverse/entrance-cas/${casId}`, body, {
      meta,
    }),
  )

export const deleteEntranceCases = async (
  body: EntranceCasDeleteBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-cas', { data: body, meta })
}

// 같은 (casId, guestId) 조합을 다시 보내면 서버가 건너뛴다. 응답(새로 만든 배정 목록)은 화면이 쓰지 않는다.
export const assignEntranceCas = async (
  body: EntranceCasAssignBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/creverse/entrance-cas/assignments', body, { meta })
}

// 무효 건이 하나라도 섞이면 서버가 전체를 되돌린다
export const unassignEntranceCas = async (
  body: EntranceCasUnassignBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-cas/assignments', { data: body, meta })
}

export const getGuestEntranceCases = async (
  guestId: Guest['id'],
  params: GuestEntranceCasListParams,
  meta?: RequestMeta,
): Promise<Slice<GuestEntranceCas>> =>
  adaptGuestEntranceCases(
    await httpClient().get<SliceCreverseGuestEntranceCasItemResponse>(
      `/creverse/entrance-cas/assignments/${guestId}`,
      { params, meta },
    ),
  )

export const getGuestEntranceCasScoringItems = async (
  guestEntranceCasId: EntranceCasAssignee['guestEntranceCasId'],
  meta?: RequestMeta,
): Promise<CasScoringItem[]> =>
  adaptCasScoringItems(
    await httpClient().get<GetScoringData>(
      `/creverse/entrance-cas/assignments/${guestEntranceCasId}/scoring`,
      { meta },
    ),
  )

/** 입학고사(자동시험) 원클릭 보고서 PDF */
export const getGuestEntranceCasReportPdf = async (
  guestEntranceCasId: GuestEntranceCas['guestEntranceCasId'],
  meta?: RequestMeta,
): Promise<HttpFile> =>
  httpClient().getFile(`/creverse/entrance-cas/assignments/${guestEntranceCasId}/report/download`, {
    meta,
  })
